import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { HttpService } from '../../core/services/http.service';
import { Country } from '../../shared/models/country.model';
import { ResponseStandard } from '../../shared/models/response-standard';
import { Certificate } from '../models/certificate';
import { CertificateAllResponse } from '../models/certificate-all.response';
import { CertificateContactsRequest } from '../models/certificate-contacts.request';
import { CertificateResponse } from '../models/certificate.response';
import { DocumentResponse } from '../models/document.response';
import { Document } from '../models/document';
import { NextStateOEARequest } from '../models/next-state-oea.request';

@Injectable({
  providedIn: 'root'
})
export class CertificateService {
  private baseUrl = `${environment.host}ms-certificate/api/v1/certificate/`;
  private _selectedCertificate: Certificate;
  fileToDownload;
  private countries: Country[];
  constructor(private httpService: HttpService) {}

  all(): Observable<CertificateAllResponse> {
    const api = `${this.baseUrl}find-all`;
    this.httpService.uri = api;
    return this.httpService.execGetJson();
  }

  findById(id: string): Observable<Certificate> {
    const api = `${this.baseUrl}${id}`;
    this.httpService.uri = api;
    return this.httpService.execGetJson().pipe(
      map(certificatesResponse => certificatesResponse.data),
      tap(certificate => (this._selectedCertificate = certificate))
    );
  }

  update(id: string, certificate): Observable<CertificateResponse> {
    const api = `${this.baseUrl}${id}`;
    certificate.id = id;
    this._selectedCertificate = certificate;
    this.httpService.uri = api;
    return this.httpService
      .execPutJson(certificate)
      .pipe(
        tap(
          (certificateResponse: CertificateResponse) =>
            (this.selectedCertificate = certificateResponse.data)
        )
      );
  }

  getCountries(): Observable<Country[]> {
    const api = `${environment.host}ms-parameter/api/v1/parameter/country`;
    this.httpService.uri = api;
    return this.httpService.execGetJson().pipe(map(response => response.data));
  }

  get selectedCertificate() {
    return this._selectedCertificate as Certificate;
  }

  set selectedCertificate(certificate: Certificate) {
    this._selectedCertificate = certificate;
  }

  get countryList() {
    return this.countries;
  }

  updateCertificateDocuments(documents: any[]) {
    if (Array.isArray(this._selectedCertificate.documents)) {
      this._selectedCertificate.documents.push(...documents);
    } else {
      this._selectedCertificate.documents = documents;
    }
  }

  addContacts(
    contact: CertificateContactsRequest
  ): Observable<ResponseStandard> {
    const api = `${this.baseUrl}contact/update-contact`;
    this.httpService.uri = api;
    return this.httpService
      .execPutJson(contact)
      .pipe(tap(() => (this._selectedCertificate.contacts = contact.contacts)));
  }

  removeDocument(id: string, document: string, index): Observable<any> {
    const api = `${this.baseUrl}document/`;
    const body = {
      id,
      listDocument: [
        {
          nameDocument: document
        }
      ]
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    this.httpService.uri = api;
    return this.httpService
      .execDeleteJson(body, headers)
      .pipe(tap(() => this._selectedCertificate.documents.splice(index, 1)));
  }

  saveDocuments(documents: FormData, id: string): Observable<DocumentResponse> {
    const api = `${this.baseUrl}document/${id}`;
    this.httpService.uri = api;
    return this.httpService
      .execPostJson(documents)
      .pipe(tap(response => this.updateCertificateDocuments(response.data)));
  }

  downloadFile(certificate: Certificate, file: Document): Observable<Blob> {
    const api = `${this.baseUrl}document/${certificate.id}/download/${file.nameDocument}`;
    const headers = new HttpHeaders({
      'Content-Type': file.contentType
    });
    this.httpService.uri = api;
    return this.httpService.execGetBlob(headers).pipe(
      tap(response => {
        const blob = new Blob([response], { type: file.contentType });
        FileSaver.saveAs(blob, file.nameDocument);
      })
    );
  }

  updateState(
    id: string,
    nextState: NextStateOEARequest
  ): Observable<ResponseStandard> {
    const api = `${this.baseUrl}update-state/${id}`;
    this.httpService.uri = api;
    return this.httpService.execPutJson(nextState);
  }

  /**
   * update the visibility of certificate
   */
  updateVisibility(id: string, visible: boolean): Observable<ResponseStandard> {
    const api = `${this.baseUrl}update-visible/${id}`;
    this.httpService.uri = api;
    const payload = {
      visible
    };
    return this.httpService.execPutJson(payload).pipe(
      tap(data => {
        this._selectedCertificate = { ...data.data };
      })
    );
  }

  updateStatusOfEdit(id: string, accept: boolean) {
    const api = `${this.baseUrl}edit/${id}?accept=${accept}`;
    this.httpService.uri = api;
    return this.httpService.execPutJson().pipe(
      tap(data => {
        this._selectedCertificate = { ...data.data };
      })
    );
  }

  checkValidity(id: string): Observable<ResponseStandard> {
    const api = `${this.baseUrl}accredited/${id}`;
    this.httpService.uri = api;
    return this.httpService.execGetJson();
  }

  getUrlCredential(uuid: string): string {
    return `${this.baseUrl}verifiable-credential/${uuid}`;
  }
}
